(function() {
  'use strict';

  const GIFT_VARIANT_ID = 40278428156041;
  const THRESHOLD_CENTS = 8000;
  const CART_ROOT = window.Shopify?.routes?.root ?? '/';

  let isUpdating = false;
  let lastTotal = 0;

  async function getCart() {
    try {
      const response = await fetch(`${CART_ROOT}cart.js`);
      if (!response.ok) throw new Error(`Fetch cart failed: ${response.status}`);
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  function hasGiftInCart(cart) {
    let giftKeys = [];
    let giftQuantity = 0;
    let hasSubscription = false;

    if (cart && cart.items) {
      for (let item of cart.items) {
        if (item.variant_id === GIFT_VARIANT_ID && item.quantity > 0) {
          giftKeys.push(item.key);
          giftQuantity += item.quantity;
        }
      }
    }

    const cartItems = document.querySelectorAll('.CartItem');
    cartItems.forEach(item => {
      const variantInput = item.querySelector(`input[name="items[][id]"][value="${GIFT_VARIANT_ID}"]`);
      if (!variantInput) {
        const planAllocation = item.querySelector('.CartItem__PlanAllocation');
        if (planAllocation) {
          hasSubscription = true;
        }
      }
    });

    return { giftKeys, giftQuantity, hasSubscription };
  }

  async function addGift() {
    const formData = { items: [{ id: GIFT_VARIANT_ID, quantity: 1 }] };
    try {
      const response = await fetch(`${CART_ROOT}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!response.ok) throw new Error(`Add failed: ${response.status}`);
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  async function setGiftQuantity(lineKey) {
    try {
      const response = await fetch(`${CART_ROOT}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 1 })
      });
      if (!response.ok) throw new Error(`Set quantity failed: ${response.status}`);
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  async function removeGift(lineKey) {
    try {
      const response = await fetch(`${CART_ROOT}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      });
      if (!response.ok) throw new Error(`Remove failed: ${response.status}`);
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  async function removeDuplicateGifts(giftKeys) {
    for (let i = 1; i < giftKeys.length; i++) {
      await removeGift(giftKeys[i]);
    }
  }

  function refreshCartDrawer() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('ajaxCart.update', { bubbles: true }));
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }, 200);
  }

  async function applyGiftLogic(source = 'unknown') {
    if (isUpdating) {
      return;
    }

    const cart = await getCart();
    if (!cart) {
      return;
    }

    const totalCents = cart.total_price;
    lastTotal = totalCents;

    const { giftKeys, giftQuantity, hasSubscription } = hasGiftInCart(cart);
    const shouldHaveGift = totalCents >= THRESHOLD_CENTS || hasSubscription;
    const isGiftOnly = cart.items.length === giftKeys.length && giftKeys.length > 0;

    if (isGiftOnly) {
      isUpdating = true;
      for (const giftKey of giftKeys) {
        await removeGift(giftKey);
      }
      refreshCartDrawer();
      isUpdating = false;
      return;
    }

    if (giftKeys.length > 1) {
      isUpdating = true;
      await removeDuplicateGifts(giftKeys);
      refreshCartDrawer();
      isUpdating = false;
      const updatedCart = await getCart();
      if (!updatedCart) {
        return;
      }
      const updatedState = hasGiftInCart(updatedCart);
      giftKeys.length = 0;
      giftKeys.push(updatedState.giftKeys[0] || null);
      giftQuantity = updatedState.giftQuantity;
    }

    if (shouldHaveGift && giftKeys.length === 0) {
      isUpdating = true;
      const result = await addGift();
      if (result) {
        refreshCartDrawer();
      }
      isUpdating = false;
    } else if (shouldHaveGift && giftKeys.length === 1 && giftQuantity > 1) {
      isUpdating = true;
      const result = await setGiftQuantity(giftKeys[0]);
      if (result) {
        refreshCartDrawer();
      }
      isUpdating = false;
    } else if (!shouldHaveGift && giftKeys.length > 0) {
      isUpdating = true;
      for (const giftKey of giftKeys) {
        await removeGift(giftKey);
      }
      refreshCartDrawer();
      isUpdating = false;
    }
  }

  const originalFetch = window.fetch;
  window.fetch = async function(...args) {
    const url = args[0] || '';
    const isCartAdd = url.includes('/cart/add.js') && args[1]?.method === 'POST';
    
    if (isCartAdd) {
      try {
        const body = args[1]?.body ? JSON.parse(args[1].body) : {};
        const items = body.items || [];
        const isGiftAdd = items.some(item => item.id === GIFT_VARIANT_ID);

        if (isGiftAdd) {
          const cart = await getCart();
          if (!cart) {
            return new Response(JSON.stringify({ error: 'Cannot add gift item directly' }), {
              status: 403,
              headers: { 'Content-Type': 'application/json' }
            });
          }

          const { hasSubscription } = hasGiftInCart(cart);
          const totalCents = cart.total_price;
          const canAddGift = totalCents >= THRESHOLD_CENTS || hasSubscription;

          if (!canAddGift) {
            return new Response(JSON.stringify({ error: 'Cannot add gift item directly' }), {
              status: 403,
              headers: { 'Content-Type': 'application/json' }
            });
          }
        }
      } catch (error) {
      }
    }

    const response = await originalFetch.apply(this, args);
    if (url.includes('/cart/') && url.match(/add|change|update/) && !isUpdating) {
      clearTimeout(window.cartDebounce);
      window.cartDebounce = setTimeout(() => applyGiftLogic('fetch'), 500);
    }
    return response;
  };

  if (window.XMLHttpRequest) {
    const originalOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url) {
      this.addEventListener('load', () => {
        if (url.includes('/cart/') && url.match(/add|change|update/) && !isUpdating) {
          clearTimeout(window.cartDebounce);
          window.cartDebounce = setTimeout(() => applyGiftLogic('xhr'), 500);
        }
      });
      originalOpen.apply(this, arguments);
    };
  }

  setInterval(async () => {
    if (Math.abs(lastTotal - THRESHOLD_CENTS) < 3000 || lastTotal === 0) {
      applyGiftLogic('interval');
    }
  }, 3000);

  document.addEventListener('cart:updated', () => applyGiftLogic('cart:updated'));
  document.addEventListener('cart:refresh', () => {
    clearTimeout(window.cartDebounce);
    window.cartDebounce = setTimeout(() => applyGiftLogic('cart:refresh'), 500);
  });

  document.readyState === 'loading' 
    ? document.addEventListener('DOMContentLoaded', () => applyGiftLogic('initial'))
    : applyGiftLogic('initial');

})();